import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { cn } from '../../../../lib/utils';
import { genId } from './constants';
import { useDragSort } from '../../hooks/useDragSort';
import {
  WIDGET_TYPE_META,
  widgetMeta,
  createDefaultWidget,
  getWidgetType,
} from '../../widgets/registry';
import {
  REPORT_COLS,
  DESIGNER_ROW_HEIGHT,
  DESIGNER_MARGIN,
  DESIGNER_CONTAINER_PADDING,
  DESIGNER_MIN_W,
  DESIGNER_MIN_H,
} from '../../utils/reportLayout';
import DesignerWidgetPreview from './DesignerWidgetPreview';

const ResponsiveGridLayout = WidthProvider(Responsive);

const ROW_HEIGHT = DESIGNER_ROW_HEIGHT;
const MARGIN = DESIGNER_MARGIN;
const CPADS = DESIGNER_CONTAINER_PADDING;
const COLS = REPORT_COLS;
const MIN_W = DESIGNER_MIN_W;
const MIN_H = DESIGNER_MIN_H;

const CATEGORIES = [
  { id: 'data',   label: 'داده' },
  { id: 'chart',  label: 'نمودار' },
];

/** Keep every item inside the 12-col grid and above minimum size */
function clampLayout(layout = [], cols = COLS) {
  return layout.map((item) => {
    const w = Math.max(MIN_W, Math.min(Number(item.w) || MIN_W, cols));
    const h = Math.max(MIN_H, Number(item.h) || MIN_H);
    const x = Math.max(0, Math.min(Number(item.x) || 0, cols - w));
    const y = Math.max(0, Number(item.y) || 0);
    return { ...item, x, y, w, h, minW: MIN_W, minH: MIN_H, maxW: cols };
  });
}

function getNextY(layout = []) {
  if (!layout.length) return 0;
  return Math.max(...layout.map((l) => (l.y || 0) + (l.h || 1)));
}

/** Place a new widget beside the last one if there is room on that row, else next row */
function findFreeSlot(layout = [], w = 6, h = 5) {
  if (!layout.length) return { x: 0, y: 0 };
  const lastY = Math.max(...layout.map((l) => l.y || 0));
  const rowItems = layout.filter((l) => (l.y || 0) === lastY);
  const usedRight = rowItems.reduce((max, l) => Math.max(max, (l.x || 0) + (l.w || 0)), 0);
  if (usedRight + w <= COLS) return { x: usedRight, y: lastY };
  return { x: 0, y: getNextY(layout) };
}

function TabLabel({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft !== value) onChange(draft.trim());
    else setDraft(value);
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setEditing(false); setDraft(value); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="rounded border border-indigo-400 bg-white px-2 py-0.5 text-sm font-medium focus:outline-none w-24"
      />
    );
  }

  return (
    <span
      onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); setDraft(value); }}
      title="دو بار کلیک برای تغییر نام"
      className="cursor-default select-none"
    >
      {value}
    </span>
  );
}

const WIDGET_BLURBS = {
  bar_chart: 'مقایسه مقادیر',
  line_chart: 'روند زمانی',
  area_chart: 'سطح زیر روند',
  composed_chart: 'چند نوع در یک نمودار',
  pie_chart: 'سهم از کل',
  radar_chart: 'چند بُعد همزمان',
  radial_bar: 'درصد شعاعی',
  scatter_chart: 'پراکندگی دو متغیر',
  table: 'فهرست ردیف‌ها با کوئری خودش',
  stat_card: 'یک کارت با مقدار، بج و خطوط',
  gauge: 'گیج هدف مدرن',
  progress_bar: 'نوار پیشرفت افقی',
};

function TypePickerModal({ onSelect, onClose }) {
  const [activeCat, setActiveCat] = useState('data');
  const filtered = WIDGET_TYPE_META.filter((w) => w.category === activeCat);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-[560px] max-w-[95vw] max-h-[80vh] flex flex-col rtl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-l from-indigo-50 to-white px-5 py-4 shrink-0">
          <div>
            <h3 className="text-base font-bold text-slate-800">افزودن ویجت</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">نوع نمایش را انتخاب کنید؛ منبع داده را بعداً تنظیم می‌کنید</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 text-lg">✕</button>
        </div>

        <div className="flex gap-1 border-b border-slate-100 px-4 pt-2 shrink-0">
          {CATEGORIES.map((c) => (
            <button key={c.id} type="button" onClick={() => setActiveCat(c.id)}
              className={cn(
                'px-4 py-1.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
                activeCat === c.id
                  ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              )}>
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2.5 overflow-y-auto p-4 sm:grid-cols-3">
          {filtered.map((wt) => (
            <button key={wt.type} type="button" onClick={() => onSelect(wt.type)}
              className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3.5 hover:bg-indigo-50 hover:border-indigo-300 hover:shadow-md transition-all text-right group">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-2xl group-hover:bg-white">{wt.icon}</span>
              <span className="text-sm font-bold text-slate-700 group-hover:text-indigo-700 leading-tight">{wt.label}</span>
              <span className="text-[10px] leading-relaxed text-slate-400">{WIDGET_BLURBS[wt.type] || ''}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function GridBackground({ show }) {
  if (!show) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: [
          `repeating-linear-gradient(
            90deg,
            transparent 0%,
            transparent calc(${100 / COLS}% - 1px),
            rgba(99,102,241,0.10) calc(${100 / COLS}% - 1px),
            rgba(99,102,241,0.10) calc(${100 / COLS}%)
          )`,
          `repeating-linear-gradient(
            180deg,
            transparent 0px,
            transparent ${ROW_HEIGHT + MARGIN[1] - 1}px,
            rgba(99,102,241,0.06) ${ROW_HEIGHT + MARGIN[1] - 1}px,
            rgba(99,102,241,0.06) ${ROW_HEIGHT + MARGIN[1]}px
          )`,
        ].join(','),
      }}
    />
  );
}

function ColumnRuler() {
  return (
    <div
      className="sticky top-0 z-10 flex bg-white/95 border-b border-slate-200 backdrop-blur-sm"
      style={{ padding: `2px ${CPADS[0]}px` }}
    >
      {Array.from({ length: COLS }).map((_, i) => (
        <div
          key={i}
          className="flex-1 text-center py-0.5 text-[10px] font-mono text-slate-400 border-r border-dashed border-slate-200 last:border-r-0"
        >
          {i + 1}
        </div>
      ))}
    </div>
  );
}

export default function DesignerCanvas({
  report,
  maxLevel = 4,
  selectedTabId,
  onSelectTab,
  onAddTab,
  onRenameTab,
  onDeleteTab,
  onToggleTabVisible,
  onReorderTabs,
  onLayoutChange,
  onAddWidget,
  onDeleteWidget,
  onDuplicateWidget,
  onCopyWidget,
  selectedWidgetId,
  onSelectWidget,
  onDeselect,
}) {
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showGuides, setShowGuides] = useState(true);
  const interactingRef = useRef(false);

  const tabs = report?.tabs || [];
  const currentTab = tabs.find((t) => t.id === selectedTabId) || tabs[0];
  const widgets = currentTab?.widgets || [];
  const layoutItems = useMemo(
    () => clampLayout(currentTab?.layout || []),
    [currentTab?.layout],
  );

  const { overIdx: tabOverIdx, dragProps: tabDragProps } = useDragSort(tabs, onReorderTabs);

  // Commit only when not mid-interaction; drag/resize stop commits explicitly.
  const handleLayoutChange = useCallback(
    (newLayout) => {
      if (!currentTab || interactingRef.current) return;
      onLayoutChange(currentTab.id, clampLayout(newLayout));
    },
    [currentTab, onLayoutChange],
  );

  const commitLayout = useCallback(
    (layout) => {
      interactingRef.current = false;
      if (currentTab) onLayoutChange(currentTab.id, clampLayout(layout));
    },
    [currentTab, onLayoutChange],
  );

  const layouts = { lg: layoutItems };

  const canvasMinHeight =
    Math.max(8, getNextY(layoutItems) + 6) * (ROW_HEIGHT + MARGIN[1]) + CPADS[1] * 2;

  const handleAddWidget = (type) => {
    setShowTypePicker(false);
    const id = genId();
    const defaultScopeLevel = maxLevel || 4;
    const entry = getWidgetType(type);
    const w = entry?.defaultLayout?.w || 6;
    const h = entry?.defaultLayout?.h || 5;
    const { x, y } = findFreeSlot(currentTab?.layout || [], w, h);
    const widget = entry
      ? entry.createDefault({ id, scopeLevel: defaultScopeLevel })
      : createDefaultWidget({
          id,
          type,
          title: widgetMeta(type).label,
          scopeLevel: defaultScopeLevel,
        });

    onAddWidget(
      currentTab?.id,
      widget,
      { i: id, x, y, w, h, minW: MIN_W, minH: MIN_H, maxW: COLS },
    );
  };

  if (!report) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-100 text-slate-400">
        <div className="text-center">
          <p className="text-4xl mb-2">📋</p>
          <p className="text-sm">یک گزارش را از نوار کناری انتخاب کنید</p>
        </div>
      </div>
    );
  }

  return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-[#eef1f6]">

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-slate-200/80 bg-white/90 px-3 pt-2 shrink-0 overflow-x-auto backdrop-blur">
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            {...tabDragProps(index)}
            className={cn(
              'group relative flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg border-b-2 cursor-pointer select-none transition-all shrink-0',
              selectedTabId === tab.id
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50',
              tabOverIdx === index && 'bg-indigo-100 border-indigo-400 border-dashed',
              !tab.visible && 'opacity-50',
            )}
            onClick={() => onSelectTab(tab.id)}
          >
            <span className="shrink-0 text-slate-300 group-hover:text-slate-400 cursor-grab text-xs leading-none">⠿</span>
            <TabLabel value={tab.label} onChange={(label) => onRenameTab(tab.id, label)} />
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onToggleTabVisible(tab.id); }}
              className={cn('shrink-0 text-xs leading-none', tab.visible !== false ? 'opacity-50 hover:opacity-100' : 'opacity-30 hover:opacity-60')}>
              {tab.visible !== false ? '👁' : '🙈'}
            </button>
            {tabs.length > 1 && (
              <button type="button"
                onClick={(e) => { e.stopPropagation(); onDeleteTab(tab.id); }}
                className="shrink-0 text-[10px] text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                ✕
              </button>
            )}
          </div>
        ))}

        <button type="button" onClick={onAddTab}
          className="shrink-0 px-3 py-1.5 text-sm font-medium text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-t-lg transition-colors">
          + تب
        </button>

        <div className="mr-auto flex items-center gap-2 pb-1">
          <span className="text-[11px] text-slate-400">{widgets.length} ویجت</span>
          <button type="button" onClick={() => setShowGuides((v) => !v)}
            className={cn(
              'px-2 py-0.5 text-xs rounded border transition-colors',
              showGuides
                ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
                : 'border-slate-200 text-slate-400 hover:bg-slate-100',
            )}>
            {showGuides ? 'پنهان شبکه' : 'نمایش شبکه'}
          </button>
        </div>
      </div>

      {/* Scroll area. dir=ltr so react-grid-layout's physical x/y math matches the
          ruler; widget text stays RTL via the inner rtl class.
          min-h-0 is required so flex child can actually scroll. */}
      <div
        dir="ltr"
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        onClick={(e) => {
          if (e.target.closest?.('.react-grid-item')) return;
          if (e.target.closest?.('.react-resizable-handle')) return;
          onDeselect?.();
        }}
      >
        <ColumnRuler />

        <div
          className="relative report-designer-grid"
          style={{ minHeight: `${canvasMinHeight}px` }}
        >
          <GridBackground show={showGuides} />

          {currentTab ? (
            <ResponsiveGridLayout
              className="layout"
              layouts={layouts}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
              cols={{ lg: COLS, md: COLS, sm: COLS, xs: COLS }}
              rowHeight={ROW_HEIGHT}
              margin={MARGIN}
              containerPadding={CPADS}
              isDraggable
              isResizable
              isBounded
              compactType="vertical"
              preventCollision={false}
              allowOverlap={false}
              resizeHandles={['se', 'e', 's']}
              draggableCancel=".widget-action,input,textarea,select,button"
              onLayoutChange={handleLayoutChange}
              onDragStart={() => { interactingRef.current = true; }}
              onDragStop={commitLayout}
              onResizeStart={() => { interactingRef.current = true; }}
              onResizeStop={commitLayout}
              draggableHandle=".drag-handle"
            >
              {widgets.map((widget) => {
                const meta = widgetMeta(widget.type);
                const isSelected = selectedWidgetId === widget.id;
                const otherTabs = tabs.filter((t) => t.id !== currentTab?.id);

                return (
                  <div
                    key={widget.id}
                    onClick={() => onSelectWidget(widget.id)}
                    className={cn(
                      'group relative h-full w-full overflow-visible transition-shadow duration-100',
                      isSelected && '!z-20',
                    )}
                  >
                    <div className="h-full min-h-0 overflow-hidden" dir="rtl">
                      <DesignerWidgetPreview widget={widget} reportQueries={report?.queries || []} />
                    </div>

                    {isSelected && (
                      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-indigo-500 shadow-lg shadow-indigo-200/50" />
                    )}

                    <div
                      dir="rtl"
                      className={cn(
                        'absolute top-1.5 inset-x-1.5 z-20 flex items-center gap-1 rounded-xl border border-slate-200/80 bg-white/95 px-1.5 py-1 shadow-sm backdrop-blur-sm select-none',
                        'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto',
                        isSelected && 'opacity-100 pointer-events-auto',
                      )}
                    >
                      <div className="drag-handle flex min-w-0 flex-1 items-center gap-1 cursor-grab active:cursor-grabbing">
                        <span className="shrink-0 text-sm leading-none text-slate-400" title="بکشید برای جابجایی">⠿</span>
                        <span className="text-sm shrink-0">{meta.icon}</span>
                        <span className="flex-1 text-[11px] font-semibold truncate min-w-0 text-slate-600">
                          {widget.title || meta.label}
                        </span>
                      </div>
                      <div className="widget-action flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onMouseDown={(e) => { e.stopPropagation(); }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onDuplicateWidget?.(currentTab.id, widget.id);
                          }}
                          className="shrink-0 rounded-md bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 hover:bg-indigo-100"
                          title="تکثیر ویجت در همین تب"
                        >
                          تکثیر
                        </button>
                        {otherTabs.length > 0 && (
                          <select
                            aria-label="کپی به تب دیگر"
                            defaultValue=""
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const target = e.target.value;
                              e.target.value = '';
                              if (target) onCopyWidget?.(currentTab.id, widget.id, target);
                            }}
                            className="max-w-[92px] rounded-md border border-slate-200 bg-white px-1 py-0.5 text-[10px] text-slate-600"
                          >
                            <option value="">کپی به تب</option>
                            {otherTabs.map((tab) => (
                              <option key={tab.id} value={tab.id}>{tab.label || 'تب'}</option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          onMouseDown={(e) => { e.stopPropagation(); }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (window.confirm('آیا از حذف این ویجت مطمئن هستید؟')) {
                              onDeleteWidget(currentTab.id, widget.id);
                            }
                          }}
                          className="shrink-0 rounded-md bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600 hover:bg-rose-100"
                          title="حذف ویجت"
                        >
                          حذف
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </ResponsiveGridLayout>
          ) : (
            <div className="flex h-48 items-center justify-center text-slate-400 text-sm">
              تبی انتخاب نشده
            </div>
          )}
        </div>
      </div>

      {/* Always-visible add bar — never scrolls away with the canvas */}
      <div className="shrink-0 flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-2.5 shadow-[0_-4px_16px_rgba(15,23,42,0.04)]">
        <span className="text-[11px] text-slate-400 hidden sm:inline">
          بکشید برای جابجایی · گوشه را برای تغییر اندازه بگیرید
        </span>
        <button
          type="button"
          onClick={() => setShowTypePicker(true)}
          className="mr-auto flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-indigo-200 hover:bg-indigo-700 active:scale-[0.98] transition-all"
        >
          <span className="text-base font-bold leading-none">＋</span>
          افزودن ویجت
        </button>
      </div>

      {showTypePicker && (
        <TypePickerModal onSelect={handleAddWidget} onClose={() => setShowTypePicker(false)} />
      )}
    </div>
  );
}
